import React, { useState, useEffect, useContext } from "react";
import { Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import AllOrderPopup from "./AllOrderPopup";
import { UserContext } from "../utils/UserContext";
import getInitials from "../utils/getInitials";

function getMonday(date) {
  const d = new Date(new Date(date).setHours(0, 0, 0, 0));
  const day = d.getDay(),
    diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff));
}

function getSunday(date) {
  const d = new Date(new Date(date).setHours(0, 0, 0, 0));
  const day = d.getDay(),
    diff = d.getDate() - day + (day === 0 ? -6 : 1) + 6;
  return new Date(d.setDate(diff));
}

const rubFormatter = new Intl.NumberFormat("ru-RU");
const formatCurrency = (value) => rubFormatter.format(Number(value) || 0);

const calculateOrderTotal = (order) => {
  if (!order) return 0;
  if (typeof order.total === "number") return order.total;
  if (!Array.isArray(order.meals)) return 0;
  return order.meals.reduce(
    (acc, meal) => acc + Number(meal.price || 0) * Number(meal.amount || 0),
    0
  );
};

const AllOrders = () => {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState([]);
  const [foundActiveWeek, setFoundActiveWeek] = useState(false);
  const [dateFrom, setDateFrom] = useState(getMonday(new Date()));
  const [dateTo, setDateTo] = useState(getSunday(new Date()));
  const [activeOrder, setActiveOrder] = useState(null);
  const [activeOrderWarning, setActiveOrderWarning] = useState(null);
  const [needUpdate, setNeedUpdate] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoading, setIsFirstLoading] = useState(true);

  const userContext = useContext(UserContext);
  const team = data || [];
  const totalTeamOrders = team.reduce(
    (acc, user) => acc + user.orders.filter((day) => day.order).length,
    0
  );
  const totalTeamPositions = team.reduce(
    (acc, user) =>
      acc +
      user.orders.reduce((ordersAcc, day) => ordersAcc + (day.order?.meals?.length || 0), 0),
    0
  );
  const totalTeamAmount = team.reduce(
    (acc, user) =>
      acc + user.orders.reduce((ordersAcc, day) => ordersAcc + calculateOrderTotal(day.order), 0),
    0
  );
  const headerDays = data?.[0]?.orders || [];
  const weekRangeLabel = `${dateFrom.toLocaleDateString("ru-RU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })} — ${dateTo.toLocaleDateString("ru-RU", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })}`;

  const navigator = useNavigate();

  useEffect(() => {
    if (!needUpdate || isFirstLoading) {
      setIsFirstLoading(false);
      return;
    }
    setIsLoading(true);

    Promise.all([
      api.getOrders({ f_datef: dateFrom, f_datet: dateTo, p: 1, s: 1000 }),
      api.getAllWeek({ date: dateFrom }),
    ])
      .then(([{ list }, { result }]) => {
        setData(list);
        setWeek(result);
      })
      .catch((err) => err.status === 403 && navigator("/access_denied"))
      .finally(() => {
        setNeedUpdate(false);
        setIsLoading(false);
      });
  }, [dateFrom, dateTo, needUpdate, isFirstLoading]);

  useEffect(() => {
    if (
      !week.length ||
      foundActiveWeek ||
      dateFrom.getMilliseconds() !== new Date(week[0]?.date).getMilliseconds()
    )
      return;
    if (new Date(week.filter((el) => !el.is_work)[0].date) < new Date()) {
      changeWeek("+");
    } else {
      setFoundActiveWeek(true);
    }
  }, [week]);

  const changeWeek = (whereTo) => {
    if (whereTo === "+") {
      const lastWeekDay = getSunday(new Date(dateFrom));
      const firstDayNextWeek = new Date(
        new Date(lastWeekDay).setDate(lastWeekDay.getDate() + 1)
      );
      setDateFrom(getMonday(firstDayNextWeek));
      setDateTo(getSunday(firstDayNextWeek));
    } else if (whereTo === "-") {
      const lastWeekDay = getMonday(new Date(dateFrom));
      const lastDayPrevWeek = new Date(
        new Date(lastWeekDay).setDate(lastWeekDay.getDate() - 1)
      );
      setDateFrom(getMonday(lastDayPrevWeek));
      setDateTo(getSunday(lastDayPrevWeek));
    }
    setNeedUpdate(true);
  };
  return (
    <>
      <div className="order-hero glass-panel team-hero">
        <div className="order-hero__content">
          <div className="spark-chip">
            <i className="bi bi-people-fill" />
            Вся еда · Команда
          </div>
          <h1 className="fw-bold mb-1">Сводная таблица заказов</h1>
          <p className="page-subtitle m-0">
            Строки — сотрудники, столбцы — дни недели. Быстро видно, кто заказал, что свободно и что
            на обмене.
          </p>
          <div className="order-hero__actions">
            <div className="week-control elevated">
              <span
                className="icon-btn"
                onClick={() => changeWeek("-")}
                role="button"
                aria-label="Предыдущая неделя"
              >
                <i className="bi bi-chevron-left" />
              </span>
              <div className="fw-semibold week-range">
                <div>{weekRangeLabel}</div>
              </div>
              <span
                className="icon-btn"
                onClick={() => changeWeek("+")}
                role="button"
                aria-label="Следующая неделя"
              >
                <i className="bi bi-chevron-right" />
              </span>
            </div>
            <span className="micro-pill tonal">
              <i className="bi bi-people" />
              Сотрудников: {team.length}
            </span>
            {isLoading ? (
              <span className="micro-pill subtle">
                <i className="bi bi-activity" />
                Обновляем...
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="order-hero__stats">
          <div className="order-hero__stat">
            <p className="label">Заказов</p>
            <p className="value">{totalTeamOrders}</p>
            <span className="hint">Создано на выбранной неделе</span>
          </div>
          <div className="order-hero__stat">
            <p className="label">Позиций</p>
            <p className="value">{totalTeamPositions}</p>
            <span className="hint">Сумма блюд по всем заказам</span>
          </div>
          <div className="order-hero__stat">
            <p className="label">Команда</p>
            <p className="value">{team.length}</p>
            <span className="hint">Активные участники</span>
          </div>
        </div>
      </div>

      {data ? (
        <div className="team-matrix glass-panel">
          <div className="matrix-scroll">
            <div
              className="matrix-grid"
              style={{
                gridTemplateColumns: `180px repeat(${headerDays.length}, minmax(320px, 1fr))`,
              }}
            >
              <div className="matrix-cell matrix-cell--head matrix-cell--corner">
                <div className="matrix-headline">
                  <i className="bi bi-people" />
                  Участник
                </div>
                <p className="matrix-hint m-0">Кто заказывает</p>
              </div>
              {headerDays.map((day, ind) => {
                const dayMeta = week[ind];
                const isWeekend = dayMeta ? !dayMeta.is_work : false;
                return (
                  <div
                    key={`${day.weekDay}-${ind}`}
                    className={`matrix-cell matrix-cell--head${isWeekend ? " is-off" : ""}`}
                  >
                    <p className="m-0 matrix-day-title">{day.weekDay}</p>
                    <p className="m-0 matrix-date">
                      {new Date(day.date).toLocaleDateString("ru-RU", {
                        day: "numeric",
                        month: "short",
                      })}
                    </p>
                    {dayMeta?.spday ? (
                      <span className="micro-pill">
                        <i className="bi bi-stars" />
                        {dayMeta.spday.name}
                      </span>
                    ) : (
                      ""
                    )}
                    {isWeekend ? <span className="micro-pill danger">Выходной</span> : ""}
                  </div>
                );
              })}

              {data.map((user) => {
                const userPositions = user.orders.reduce(
                  (acc, day) => acc + (day.order?.meals?.length || 0),
                  0
                );
                const userDays = user.orders.filter((day) => day.order).length;
                return (
                  <React.Fragment key={user._id}>
                    <div className="matrix-cell matrix-cell--name">
                      <div className="matrix-person vertical">
                            <div className="team-avatar small">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: 12,
                                    background: "linear-gradient(135deg, #6cafe9, #5fd1ff)",
                                    color: "#ffffff",
                                    fontWeight: 800,
                                    fontSize: 24,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textTransform: "uppercase",
                                  }}
                            >
                              {getInitials(user.name) || "?"}
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="m-0 fw-bold">{user.name}</p>
                          <p className="m-0 matrix-hint">
                            {userDays} дней · {userPositions} позиций
                          </p>
                        </div>
                      </div>
                    </div>

                    {user.orders.map((day, ind) => {
                      const dayMeta = week[ind];
                      const isWeekend = dayMeta ? !dayMeta.is_work : false;
                      const isBlocked =
                        ((dayMeta?.denyOrderApplication && dayMeta?.is_ordered) ||
                          (dayMeta?.denyOrderWeekend && !dayMeta?.is_work)) &&
                        !userContext.isRoot;
                      const hasOrder = Boolean(day.order);
                      const exchangeMeals =
                        day.order?.meals?.filter((meal) => (meal.onChangeAmount || 0) > 0).length ||
                        0;
                      const orderTotal = calculateOrderTotal(day.order);

                      const statusBlock = hasOrder ? (
                        <div className="matrix-status">
                          <span
                            className={`status-pill ${day.order.status === "ordered" ? "" : "muted"}`}
                          >
                            {day.order.status === "ordered" ? "Заказ" : "Заявка"}
                          </span>
                          <span className="matrix-meta">{day.order.meals.length} поз.</span>
                          <span className="matrix-meta">
                            <i className="bi bi-cash-stack" />
                            {" "}{formatCurrency(orderTotal)} ₽
                          </span>
                          {exchangeMeals ? (
                            <span className="matrix-meta accent">
                              <i className="bi bi-arrow-repeat" />
                              {exchangeMeals} на обмене
                            </span>
                          ) : (
                            ""
                          )}
                        </div>
                      ) : (
                        <div className="matrix-status">
                          <span className="status-pill muted">
                            {isBlocked ? (isWeekend ? "Выходной" : "Занят") : "Свободно"}
                          </span>
                        </div>
                      );

                      const bodyBlock = hasOrder ? (
                        <div className="matrix-list">
                          {day.order.meals.slice(0, 2).map((meal) => (
                            <div
                              className={`matrix-list__item${
                                (meal.onChangeAmount || 0) > 0 ? " on-exchange" : ""
                              }`}
                              key={meal._id}
                            >
                              <span className="name">{meal.name}</span>
                              <span className="meta">
                                {meal.amount} × {meal.price} ₽
                              </span>
                            </div>
                          ))}
                          {day.order.meals.length > 2 ? (
                            <div className="matrix-list__item muted">
                              +{day.order.meals.length - 2} еще
                            </div>
                          ) : (
                            ""
                          )}
                        </div>
                      ) : (
                        <div className="matrix-empty">
                          <div>
                            <p className="fw-semibold mb-1">
                              {isBlocked ? "День закрыт" : "Свободно"}
                            </p>
                            <p className="text-muted m-0">
                              {isBlocked
                                ? isWeekend
                                  ? "Выходной день"
                                  : "Заявка уже создана"
                                : "Можно создать заявку за сотрудника"}
                            </p>
                          </div>
                        </div>
                      );

                      const actionsBlock = hasOrder ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="ghost-btn"
                          onClick={() => setActiveOrder(day.order._id)}
                        >
                          Детали
                        </Button>
                      ) : isBlocked ? (
                        <span className="matrix-meta muted">Недоступно</span>
                      ) : (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="ghost-btn"
                          onClick={() => {
                            if (dayMeta?.is_ordered || !dayMeta?.is_work) {
                              setActiveOrderWarning({
                                reason: !dayMeta?.is_work
                                  ? "weekend"
                                  : dayMeta?.is_ordered
                                  ? "ordered"
                                  : "unknown",
                                activeOrder: {
                                  new: "new",
                                  date: day.date,
                                  user: user._id,
                                },
                              });
                            } else {
                              setActiveOrder({
                                new: "new",
                                date: day.date,
                                user: user._id,
                              });
                            }
                          }}
                        >
                          Создать
                        </Button>
                      );

                      return (
                        <div
                          key={`${user._id}-${day.date}-${ind}`}
                          className={`matrix-cell${isWeekend ? " is-off" : ""}${
                            hasOrder ? " has-order" : ""
                          }${isBlocked && !hasOrder ? " is-blocked" : ""}`}
                        >
                          {statusBlock}
                          <div className="matrix-body">{bodyBlock}</div>
                          <div className="matrix-actions">{actionsBlock}</div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
      <AllOrderPopup
        activeOrder={activeOrder}
        setActiveOrder={setActiveOrder}
        setNeedUpdate={setNeedUpdate}
        needUpdate={needUpdate}
      />
      <Modal
        show={!!activeOrderWarning}
        onHide={() => setActiveOrderWarning(null)}
      >
        <Modal.Header closeButton>{`${
          activeOrderWarning?.reason === "weekend"
            ? "Это выходной."
            : activeOrderWarning?.reason === "ordered"
            ? "На этот день уже создана заявка."
            : ""
        } Вы уверены, что хотите создать заказ?`}</Modal.Header>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveOrderWarning(null);
              setActiveOrder(activeOrderWarning.activeOrder);
            }}
          >
            Создать заказ
          </Button>
          <Button
            onClick={() => {
              setActiveOrderWarning(null);
            }}
          >
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AllOrders;
